export const DOCTOR_SALUTATION_OPTIONS = Object.freeze([
  "",
  "Dr.",
  "PharmD",
  "Prof.",
  "Rn.",
  "Mr.",
  "Mrs.",
  "Miss",
  "Ms.",
  "Mme.",
]);
const DOCTOR_SALUTATIONS = new Set(DOCTOR_SALUTATION_OPTIONS.filter(Boolean));
const PATIENT_SALUTATIONS = new Set(["Mr.", "Mrs.", "Ms.", "Miss", "Mme.", "Prof."]);

export function profileText(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.toLowerCase() === "[object object]" ? "" : trimmed;
  }
  if (typeof value === "number") return String(value);
  return "";
}

export function normalizeProfileSalutation(role, value) {
  const raw = profileText(value);
  const allowed = role === "doctor" ? DOCTOR_SALUTATIONS : PATIENT_SALUTATIONS;
  return allowed.has(raw) ? raw : "";
}

export function formatProfileNamePart(value) {
  return profileText(value)
    .split(/([\s-]+)/)
    .map((part) => {
      if (!part || /^[\s-]+$/.test(part)) return part;
      const lettersOnly = part.replace(/[^\p{L}]/gu, "");
      const isUniformCase =
        lettersOnly &&
        (lettersOnly === lettersOnly.toLocaleLowerCase() ||
          lettersOnly === lettersOnly.toLocaleUpperCase());
      if (!isUniformCase) return part;

      return part
        .toLocaleLowerCase()
        .replace(/(^|['’])\p{L}/gu, (match) => match.toLocaleUpperCase());
    })
    .join("");
}

function formatProfileDisplayName(value) {
  return profileText(value)
    .split(/(\s+)/)
    .map((part) => (/^\s+$/.test(part) ? part : formatProfileNamePart(part)))
    .join("");
}

export function resolveProfileFamilyName(...rows) {
  for (const row of rows) {
    if (!row) continue;
    const explicit =
      profileText(row.last_name) ||
      profileText(row.family_name) ||
      profileText(row.surname);
    if (explicit) return explicit;

    const fullName = profileText(row.full_name || row.display_name || row.name);
    const parts = fullName.split(/\s+/).filter(Boolean);
    if (parts.length > 1) return parts.slice(-1)[0];
  }
  return "";
}

export function resolveAddressedProfileName(role, row, fallback = "") {
  if (role === "doctor") {
    return resolveCanonicalClinicianDisplayName(row, fallback);
  }
  const salutation = normalizeProfileSalutation(role, row?.salutation);
  const familyName = resolveProfileFamilyName(row);
  if (salutation && familyName) {
    return `${salutation} ${formatProfileNamePart(familyName)}`.trim();
  }
  return profileText(fallback);
}

/**
 * Canonical clinician presentation boundary.
 *
 * It never writes back to the stored profile. A configured salutation and
 * family name remain authoritative; otherwise the best stored display name is
 * presented with conservative casing normalisation. Mixed-case names are
 * preserved, while uniformly lower/upper-case tokens are made readable.
 */
export function resolveCanonicalClinicianDisplayName(row, fallback = "Clinician") {
  const salutation = normalizeProfileSalutation("doctor", row?.salutation);
  const familyName = resolveProfileFamilyName(row);
  if (salutation && familyName) {
    return `${salutation} ${formatProfileNamePart(familyName)}`.trim();
  }

  const displayName =
    profileText(row?.display_name) ||
    profileText(row?.full_name) ||
    [profileText(row?.first_name), profileText(row?.last_name)].filter(Boolean).join(" ") ||
    profileText(row?.name) ||
    profileText(fallback);

  return formatProfileDisplayName(displayName);
}

// Backwards-compatible product-language alias for callers that still use
// "doctor" while the shared presentation contract uses "clinician".
export const resolveCanonicalDoctorDisplayName = resolveCanonicalClinicianDisplayName;
